import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { BarChart3, Calendar, TrendingUp, Users, Award } from "lucide-react";

const ClubAnalytics = ({ user }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({
    start: "",
    end: ""
  });
  
  // Calculate preset date ranges
  const getThisWeek = () => {
    const today = new Date();
    const firstDay = new Date(today.setDate(today.getDate() - today.getDay()));
    const lastDay = new Date(today.setDate(today.getDate() - today.getDay() + 6));
    return {
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0]
    };
  };

  const getThisMonth = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0]
    };
  };

  const getThisYear = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), 0, 1);
    const lastDay = new Date(today.getFullYear(), 11, 31);
    return {
      start: firstDay.toISOString().split('T')[0],
      end: lastDay.toISOString().split('T')[0]
    };
  };

  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const options = { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  const fetchAnalytics = async (start = "", end = "") => {
    setLoading(true);
    try {
      let url = `${API}/club-analytics`;
      const params = [];
      if (start) params.push(`start_date=${start}`);
      if (end) params.push(`end_date=${end}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setAnalytics(response.data);
    } catch (error) {
      toast.error("Failed to fetch analytics");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load this week by default
    const week = getThisWeek();
    setDateRange(week);
    fetchAnalytics(week.start, week.end);
  }, []);

  const handlePresetRange = (range) => {
    let dates;
    if (range === 'week') dates = getThisWeek();
    else if (range === 'month') dates = getThisMonth();
    else if (range === 'year') dates = getThisYear();
    
    setDateRange(dates);
    fetchAnalytics(dates.start, dates.end);
  };

  const handleCustomRange = () => {
    if (!dateRange.start || !dateRange.end) {
      toast.error("Please select both start and end dates");
      return;
    }
    fetchAnalytics(dateRange.start, dateRange.end);
  };

  if (!analytics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Selector */}
      <Card className="border-amber-100 shadow-md">
        <CardHeader>
          <CardTitle className="text-xl text-amber-600 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Select Date Range
          </CardTitle>
          <CardDescription>View analytics for different time periods</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="week" className="w-full">
            <TabsList className="grid w-full grid-cols-4 mb-4">
              <TabsTrigger value="week" onClick={() => handlePresetRange('week')} data-testid="analytics-this-week">
                This Week
              </TabsTrigger>
              <TabsTrigger value="month" onClick={() => handlePresetRange('month')} data-testid="analytics-this-month">
                This Month
              </TabsTrigger>
              <TabsTrigger value="year" onClick={() => handlePresetRange('year')} data-testid="analytics-this-year">
                This Year
              </TabsTrigger>
              <TabsTrigger value="custom" data-testid="analytics-custom">Custom</TabsTrigger>
            </TabsList>
            
            <TabsContent value="custom" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start-date">Start Date</Label>
                  <Input
                    id="start-date"
                    data-testid="analytics-start-date"
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                    className="border-amber-200"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="end-date">End Date</Label>
                  <Input
                    id="end-date"
                    data-testid="analytics-end-date"
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                    className="border-amber-200"
                  />
                </div>
              </div>
              <Button 
                onClick={handleCustomRange} 
                data-testid="analytics-apply-custom"
                className="w-full bg-amber-600 hover:bg-amber-700"
                disabled={loading}
              >
                {loading ? "Loading..." : "Apply Date Range"}
              </Button>
            </TabsContent>
          </Tabs>
          
          <div className="mt-4 text-sm text-gray-600 bg-gray-50 p-3 rounded">
            Showing data from <span className="font-semibold">{formatDateDisplay(dateRange.start)}</span> to <span className="font-semibold">{formatDateDisplay(dateRange.end)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-green-200 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardDescription className="text-xs flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Total Approved Hours
            </CardDescription>
            <CardTitle className="text-4xl font-bold text-green-600">{analytics.total_hours}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card className="border-blue-200 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardDescription className="text-xs flex items-center gap-2">
              <Users className="w-4 h-4" />
              Active Volunteers
            </CardDescription>
            <CardTitle className="text-4xl font-bold text-blue-600">{analytics.total_volunteers}</CardTitle>
          </CardHeader>
        </Card>
        
        <Card className="border-purple-200 shadow-md hover:shadow-lg transition-shadow">
          <CardHeader className="pb-3">
            <CardDescription className="text-xs flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Total Entries
            </CardDescription>
            <CardTitle className="text-4xl font-bold text-purple-600">{analytics.total_entries}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Activity Breakdown */}
        <Card className="border-amber-100 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-amber-600 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Hours by Activity
            </CardTitle>
            <CardDescription>Breakdown of volunteer hours by activity type</CardDescription>
          </CardHeader>
          <CardContent>
            {Object.keys(analytics.activity_breakdown).length === 0 ? (
              <p className="text-center text-gray-500 py-8">No activity data for this period</p>
            ) : (
              <div className="space-y-3">
                {Object.entries(analytics.activity_breakdown).map(([activity, hours]) => (
                  <div key={activity} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <span className="font-medium capitalize text-gray-700">{activity}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-amber-600 h-2 rounded-full" 
                          style={{ width: `${(hours / analytics.total_hours) * 100}%` }}
                        ></div>
                      </div>
                      <span className="font-bold text-amber-600 min-w-[60px] text-right">{hours}h</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Volunteers */}
        <Card className="border-red-100 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-red-600 flex items-center gap-2">
              <Award className="w-5 h-5" />
              Top Volunteers
            </CardTitle>
            <CardDescription>Most active volunteers in this period</CardDescription>
          </CardHeader>
          <CardContent>
            {analytics.top_volunteers.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No volunteer data for this period</p>
            ) : (
              <div className="space-y-2">
                {analytics.top_volunteers.map((volunteer, index) => (
                  <div 
                    key={volunteer.name} 
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      index === 0 ? 'bg-yellow-50 border-yellow-300' :
                      index === 1 ? 'bg-gray-50 border-gray-300' :
                      index === 2 ? 'bg-orange-50 border-orange-300' :
                      'bg-white border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className={`text-lg font-bold ${
                        index === 0 ? 'text-yellow-600' :
                        index === 1 ? 'text-gray-600' :
                        index === 2 ? 'text-orange-600' :
                        'text-gray-400'
                      }`}>
                        #{index + 1}
                      </span>
                      <span className="font-medium text-gray-700">{volunteer.name}</span>
                    </div>
                    <span className="font-bold text-red-600">{volunteer.hours}h</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ClubAnalytics;
